import { useEffect, useState } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import toast from "react-hot-toast";
import { crearCompromiso, actualizarCompromiso } from "../../utils/compromisosPago";
import { generarCuotas, validarSumaCuotas } from "../../utils/cuotasUtil";
import {
  getPlanPagosCompromiso,
  crearPlanPagosCompromiso,
  actualizarCuotaCompromiso,
  eliminarCuotaCompromiso,
} from "../../utils/planPagosCompromiso";
import { getProveedores } from "../../utils/proveedores";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import styles from "./CompromisoPagoModal.module.scss";

// Alta/edición de un COMPROMISO DE PAGO (egreso futuro a un proveedor SIN factura todavía).
// Ver docs/dominios/compromisos-pago.md. Persiste con los utils de Fase 2:
//   crearCompromiso / actualizarCompromiso + (opcional) plan_pagos_compromiso.
// El plan de cuotas REUSA el patrón de PlanPagosActivoModal (generarCuotas/validarSumaCuotas,
// tabla editable, guardado selectivo que nunca toca cuotas pagadas). created_by = usuario real (10.104).

const HOY = new Date().toISOString().split("T")[0];
const MONEDAS = ["CLP", "PEN", "USD", "UF"];
const esPagada = (c) => c.estado === "pagada";
const normalizarFecha = (f) => (f && String(f).includes("T") ? String(f).substring(0, 10) : f || "");

// `permitirElegirProveedor`: en el alta suelta desde el header de FlujoCajaV2 el modal muestra un
// selector de proveedor OPCIONAL (patrón del select de la familia activos: opción "Sin proveedor"
// vacía + lista de proveedores del país). El camino desde la ficha del proveedor (EstadoCuenta) NO
// pasa el prop → sin selector, proveedor fijo por `proveedorId`, idéntico a antes.
const CompromisoPagoModal = ({
  show,
  onHide,
  pais,
  proveedorId,
  proveedorNombre,
  compromiso,
  userId,
  onSaved,
  permitirElegirProveedor = false,
}) => {
  const editando = !!compromiso?.id;
  const monedaDefault = pais === "Chile" ? "CLP" : "PEN";

  const [proveedores, setProveedores] = useState([]);
  const [proveedorSel, setProveedorSel] = useState(""); // id del proveedor elegido ("" = sin proveedor)
  const [concepto, setConcepto] = useState("");
  const [monto, setMonto] = useState("");
  const [moneda, setMoneda] = useState(monedaDefault);
  const [exentoIva, setExentoIva] = useState(false);
  const [fechaEstimada, setFechaEstimada] = useState("");
  const [conPlan, setConPlan] = useState(false);
  const [cuotas, setCuotas] = useState([]);
  const [cuotasOriginales, setCuotasOriginales] = useState([]);
  const [numCuotas, setNumCuotas] = useState(3);
  const [frecuencia, setFrecuencia] = useState("mensual");
  const [fechaInicio, setFechaInicio] = useState(HOY);
  const [guardando, setGuardando] = useState(false);
  const [tocado, setTocado] = useState(false); // marca el submit intentado → borde rojo (10.158)

  // Reset/precarga en cada apertura. En edición: campos del compromiso + su plan existente.
  useEffect(() => {
    if (!show) return;
    setTocado(false);
    // Proveedor: precarga el elegido (edición o prop fija) y, si el selector está habilitado,
    // trae la lista de proveedores del país. Sin el flag, el selector no se muestra y no se fetchea.
    setProveedorSel(
      compromiso?.proveedor_id != null
        ? String(compromiso.proveedor_id)
        : proveedorId != null
          ? String(proveedorId)
          : ""
    );
    if (permitirElegirProveedor) {
      (async () => {
        try {
          setProveedores(await getProveedores(pais));
        } catch (e) {
          toast.error(`Error al cargar proveedores: ${e.message || e}`);
        }
      })();
    }
    setNumCuotas(3);
    setFrecuencia("mensual");
    setFechaInicio(HOY);
    if (compromiso?.id) {
      setConcepto(compromiso.concepto || "");
      setMonto(String(compromiso.monto ?? ""));
      setMoneda(compromiso.moneda || monedaDefault);
      setExentoIva(!!compromiso.exento_iva);
      setFechaEstimada(normalizarFecha(compromiso.fecha_estimada));
      (async () => {
        try {
          const plan = await getPlanPagosCompromiso(compromiso.id);
          const norm = plan.map((c) => ({
            ...c,
            monto_cuota: Number(c.monto_cuota),
            fecha_vencimiento: normalizarFecha(c.fecha_vencimiento),
          }));
          setCuotas(norm);
          setCuotasOriginales(norm);
          setConPlan(norm.length > 0);
        } catch (e) {
          toast.error(`Error al cargar el plan: ${e.message || e}`);
        }
      })();
    } else {
      setConcepto("");
      setMonto("");
      setMoneda(monedaDefault);
      setExentoIva(false);
      setFechaEstimada("");
      setConPlan(false);
      setCuotas([]);
      setCuotasOriginales([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, compromiso?.id]);

  const montoNum = Number(monto || 0);
  const sumaCuotas = cuotas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
  const cuadra = validarSumaCuotas(cuotas, montoNum);
  const faltaFecha = !fechaEstimada;
  const fmt = (n) => `${moneda} ${Number(n || 0).toLocaleString("es-CL", { maximumFractionDigits: 2 })}`;

  const puedeGuardar =
    !guardando && !!concepto.trim() && montoNum > 0 && !!fechaEstimada && (!conPlan || (cuotas.length > 0 && cuadra));

  // Generador de conveniencia sobre el monto restante (total − pagadas), reusando cuotasUtil.
  const handleGenerar = () => {
    const n = Math.trunc(Number(numCuotas));
    if (!Number.isFinite(n) || n < 1) return toast.error("Ingresá al menos 1 cuota");
    if (montoNum <= 0) return toast.error("Cargá el monto total antes de generar cuotas");
    if (!fechaInicio) return toast.error("Elegí una fecha de inicio");
    const pagadas = cuotas.filter(esPagada);
    const restante = montoNum - pagadas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
    const nuevas = generarCuotas(restante, n, fechaInicio, frecuencia, true).map((c) => ({
      ...c,
      estado: "pendiente",
    }));
    setCuotas([...pagadas, ...nuevas].map((c, i) => ({ ...c, numero_cuota: i + 1 })));
  };

  const handleCambiarCuota = (idx, field, value) =>
    setCuotas((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));

  const handleAgregarFila = () =>
    setCuotas((prev) => [
      ...prev,
      { numero_cuota: prev.length + 1, monto_cuota: 0, fecha_vencimiento: HOY, estado: "pendiente" },
    ]);

  const handleQuitarFila = (idx) => setCuotas((prev) => prev.filter((_, i) => i !== idx));

  const handleGuardar = async () => {
    setTocado(true);
    if (!concepto.trim()) return toast.error("El concepto es obligatorio");
    if (montoNum <= 0) return toast.error("El monto debe ser mayor a 0");
    if (!fechaEstimada) return toast.error("La fecha estimada de pago es obligatoria");
    if (conPlan && cuotas.length === 0) return toast.error("Agregá al menos una cuota o desactivá el plan");
    if (conPlan && !cuadra) return toast.error(`Las cuotas (${fmt(sumaCuotas)}) no suman el monto (${fmt(montoNum)})`);

    setGuardando(true);
    try {
      const campos = {
        concepto: concepto.trim(),
        monto: montoNum,
        moneda,
        // Alta sin proveedor (botón "+"): sin desglose de IVA, el monto es el TOTAL a gastar → exento
        // (exento_iva=true) para que la proyección lo use tal cual, sin grossear. El alta con proveedor
        // (ficha) mantiene el afecto/exento elegido.
        exento_iva: permitirElegirProveedor ? true : exentoIva,
        fecha_estimada: fechaEstimada,
      };

      // Proveedor efectivo: con selector habilitado, el elegido ("" → null = sin proveedor);
      // sin selector (alta desde la ficha del proveedor), el prop fijo. proveedor_id nullable
      // desde la migración 20260823 → un compromiso puede nacer sin proveedor.
      const proveedorIdEfectivo = permitirElegirProveedor ? (proveedorSel ? Number(proveedorSel) : null) : proveedorId;

      let compromisoId = compromiso?.id;
      let creado = null;
      if (editando) {
        // F1 (B.2b, 2026-09-26): DATO_DOCUMENTO PROVISIONAL — esta ficha (ABM completo del
        // compromiso) no valida hoy piso ni día hábil sobre fecha_estimada; ESTRICTO introduciría
        // una restricción nueva. pendiente Fase 5.
        await actualizarCompromiso(compromisoId, campos, { modo: MODOS_VALIDACION.DATO_DOCUMENTO });
      } else {
        creado = await crearCompromiso(
          {
            ...campos,
            pais,
            proveedor_id: proveedorIdEfectivo,
            created_by: userId,
          },
          { modo: MODOS_VALIDACION.DATO_DOCUMENTO }
        );
        compromisoId = creado.id;
      }

      // Plan de pagos — guardado SELECTIVO (espejo de PlanPagosActivoModal): nunca toca pagadas.
      if (conPlan) {
        const pendientesState = cuotas.filter((c) => !esPagada(c));
        const originalesPendientes = cuotasOriginales.filter((c) => !esPagada(c));
        const maxNumero = cuotasOriginales.reduce((m, c) => Math.max(m, Number(c.numero_cuota) || 0), 0);
        const ops = [];

        // UPDATE: pendientes con id, solo si cambió fecha o monto.
        for (const c of pendientesState) {
          if (!c.id) continue;
          const orig = cuotasOriginales.find((o) => o.id === c.id);
          const cambios = {};
          if (orig && String(orig.fecha_vencimiento) !== String(c.fecha_vencimiento))
            cambios.fecha_vencimiento = c.fecha_vencimiento;
          if (orig && Number(orig.monto_cuota) !== Number(c.monto_cuota)) cambios.monto_cuota = Number(c.monto_cuota);
          if (Object.keys(cambios).length > 0)
            ops.push(actualizarCuotaCompromiso(c.id, cambios, { modo: MODOS_VALIDACION.DATO_DOCUMENTO }));
        }

        // INSERT: pendientes nuevas (sin id). numero_cuota estable = max existente + secuencial.
        const nuevas = pendientesState.filter((c) => !c.id);
        if (nuevas.length > 0) {
          ops.push(
            crearPlanPagosCompromiso(
              compromisoId,
              nuevas.map((c, idx) => ({
                pais,
                numero_cuota: maxNumero + idx + 1,
                monto_cuota: Number(c.monto_cuota),
                fecha_vencimiento: c.fecha_vencimiento,
                estado: "pendiente",
                created_by: userId,
              })),
              { modo: MODOS_VALIDACION.DATO_DOCUMENTO }
            )
          );
        }

        // DELETE: pendientes que estaban en DB y el usuario quitó (el util guarda solo-pendiente).
        const idsEnState = new Set(pendientesState.filter((c) => c.id).map((c) => c.id));
        for (const o of originalesPendientes) {
          if (o.id && !idsEnState.has(o.id)) ops.push(eliminarCuotaCompromiso(o.id));
        }

        await Promise.all(ops);
      } else if (editando && cuotasOriginales.some((c) => !esPagada(c) && c.id)) {
        // Plan desactivado en edición → borrar las cuotas pendientes existentes (las pagadas quedan).
        await Promise.all(
          cuotasOriginales.filter((c) => !esPagada(c) && c.id).map((c) => eliminarCuotaCompromiso(c.id))
        );
      }

      toast.success(editando ? "Compromiso actualizado" : "Compromiso creado");
      // Patch puntual (Fase 2C): el caller reubica la fila sin refetch. En el alta pasa el row creado
      // (con su id real), el nombre del proveedor elegido (para el embed que usa la proyección) y si
      // nació con plan de cuotas (→ el caller hace refetch DIRIGIDO de ese compromiso, no del panel).
      const provNombre =
        permitirElegirProveedor && proveedorSel
          ? proveedores.find((p) => String(p.id) === String(proveedorSel))?.nombre || null
          : proveedorNombre || null;
      onSaved?.({ editando, creado, proveedorNombre: provNombre, conPlan });
      onHide?.();
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>
          {editando ? "Editar compromiso de pago" : "Nuevo compromiso de pago"}
          {proveedorNombre ? ` — ${proveedorNombre}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.form}>
          {permitirElegirProveedor && (
            <div>
              <label className={styles.label}>Proveedor (opcional)</label>
              <Form.Select
                size="sm"
                value={proveedorSel}
                onChange={(e) => setProveedorSel(e.target.value)}
                disabled={guardando}
              >
                <option value="">Sin proveedor</option>
                {proveedores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </Form.Select>
            </div>
          )}

          <div>
            <label className={styles.label}>Concepto</label>
            <Form.Control
              type="text"
              size="sm"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder="Ej. Instalación tótems Piso 3"
              disabled={guardando}
              isInvalid={tocado && !concepto.trim()}
            />
          </div>

          <div className={styles.row2}>
            <div className={styles.grow}>
              {/* Alta sin proveedor ("+"): "Monto" es el TOTAL a gastar, sin desglose de IVA. */}
              <label className={styles.label}>{permitirElegirProveedor ? "Monto" : "Monto (neto)"}</label>
              <Form.Control
                type="number"
                size="sm"
                min={0}
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                placeholder="0"
                disabled={guardando}
                isInvalid={tocado && montoNum <= 0}
              />
            </div>
            <div>
              <label className={styles.label}>Moneda</label>
              <Form.Select
                size="sm"
                value={moneda}
                onChange={(e) => setMoneda(e.target.value)}
                disabled={guardando}
                style={{ width: 100 }}
              >
                {MONEDAS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Select>
            </div>
            {/* IVA solo en el alta CON proveedor (ficha). Sin proveedor ("+") el monto va tal cual (exento). */}
            {!permitirElegirProveedor && (
              <div>
                <label className={styles.label}>IVA</label>
                <Form.Select
                  size="sm"
                  value={exentoIva ? "exento" : "afecto"}
                  onChange={(e) => setExentoIva(e.target.value === "exento")}
                  disabled={guardando}
                  style={{ width: 120 }}
                >
                  <option value="afecto">Afecto</option>
                  <option value="exento">Exento</option>
                </Form.Select>
              </div>
            )}
          </div>

          <div className={styles.row2}>
            <div className={styles.grow}>
              <label className={styles.label}>Fecha estimada de pago</label>
              <Form.Control
                type="date"
                size="sm"
                value={fechaEstimada}
                onChange={(e) => setFechaEstimada(e.target.value)}
                disabled={guardando}
                isInvalid={tocado && faltaFecha}
              />
              <Form.Control.Feedback type="invalid">La fecha estimada es obligatoria.</Form.Control.Feedback>
            </div>
          </div>

          <div>
            <Form.Check
              type="checkbox"
              id="compromiso-con-plan"
              label="Cargar plan de pagos en cuotas (opcional)"
              checked={conPlan}
              onChange={(e) => setConPlan(e.target.checked)}
              disabled={guardando}
            />
          </div>

          {conPlan && (
            <>
              <div className={styles.planBox}>
                <Form.Group>
                  <label className={styles.label}>N° cuotas</label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={1}
                    value={numCuotas}
                    onChange={(e) => setNumCuotas(e.target.value)}
                    disabled={guardando}
                    style={{ width: 90 }}
                  />
                </Form.Group>
                <Form.Group>
                  <label className={styles.label}>Frecuencia</label>
                  <Form.Select
                    size="sm"
                    value={frecuencia}
                    onChange={(e) => setFrecuencia(e.target.value)}
                    disabled={guardando}
                    style={{ width: 130 }}
                  >
                    <option value="semanal">Semanal</option>
                    <option value="quincenal">Quincenal</option>
                    <option value="mensual">Mensual</option>
                    <option value="bimestral">Bimestral</option>
                    <option value="trimestral">Trimestral</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <label className={styles.label}>Fecha inicio</label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    disabled={guardando}
                    style={{ width: 160 }}
                  />
                </Form.Group>
                <Button variant="outline-secondary" size="sm" onClick={handleGenerar} disabled={guardando}>
                  Generar cuotas
                </Button>
              </div>

              <div className={styles.cuotasScroll}>
                <Table size="sm" bordered style={{ fontSize: "0.85rem", marginBottom: 8 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Vencimiento</th>
                      <th>Monto ({moneda})</th>
                      <th style={{ width: 44 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {cuotas.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", color: "#888" }}>
                          Sin cuotas. Generá o agregá filas.
                        </td>
                      </tr>
                    ) : (
                      cuotas.map((c, idx) => {
                        const bloqueada = esPagada(c);
                        return (
                          <tr key={c.id || `nueva-${idx}`}>
                            <td>{idx + 1}</td>
                            <td>
                              <Form.Control
                                type="date"
                                size="sm"
                                value={normalizarFecha(c.fecha_vencimiento)}
                                onChange={(e) => handleCambiarCuota(idx, "fecha_vencimiento", e.target.value)}
                                disabled={bloqueada || guardando}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                size="sm"
                                value={c.monto_cuota}
                                onChange={(e) => handleCambiarCuota(idx, "monto_cuota", e.target.value)}
                                disabled={bloqueada || guardando}
                              />
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {bloqueada ? (
                                <span style={{ color: "#888", fontSize: "0.75rem" }}>pagada</span>
                              ) : (
                                <Button
                                  variant="link"
                                  size="sm"
                                  style={{ color: "#c62828", padding: 0, textDecoration: "none" }}
                                  onClick={() => handleQuitarFila(idx)}
                                  disabled={guardando}
                                >
                                  ✕
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              <div className={styles.sumaRow}>
                <Button variant="outline-secondary" size="sm" onClick={handleAgregarFila} disabled={guardando}>
                  + Agregar cuota
                </Button>
                <div className={`${styles.suma} ${cuadra ? styles.ok : styles.bad}`}>
                  Σ cuotas: {fmt(sumaCuotas)} / {fmt(montoNum)}
                  {!cuadra && <span style={{ marginLeft: 8, fontWeight: 400 }}>(no cuadra)</span>}
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={!puedeGuardar}>
          {guardando ? "Guardando…" : editando ? "Guardar cambios" : "Crear compromiso"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CompromisoPagoModal;
